import type { ApiClient } from '../client/api-client';
import type {
  AssignDiscrepancyPayload,
  AssignSuspensePayload,
  CommentDiscrepancyPayload,
  EnqueueReconciliationRunPayload,
  NetworkCostGroupDto,
  NetworkCostPolicyDto,
  ReconciliationCaseDto,
  ReconciliationChallengeDto,
  ReconciliationChallengeRequest,
  ReconciliationAdjustmentConfirmPayload,
  ReconciliationAdjustmentConfirmResult,
  ReconciliationAdjustmentPreviewPayload,
  ReconciliationCasesQuery,
  ReconciliationCursorPage,
  ReconciliationDecisionDto,
  ReconciliationRunDto,
  ReconciliationRunRowsQuery,
  ReconciliationRunsQuery,
  ReconciliationSnapshotDto,
  ResolveDiscrepancyPayload,
  SuspenseItemDto,
  SuspenseListQuery,
  NetworkCostsQuery,
  UpdateNetworkCostPolicyPayload,
} from '../contracts/reconciliation';

const adminBase = '/app/v1/admin';

const buildQuery = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export function createReconciliationService(client: ApiClient) {
  return {
    listRuns: (params: ReconciliationRunsQuery = {}) =>
      client.get<ReconciliationCursorPage<ReconciliationRunDto>>(
        `${adminBase}/reconciliation/runs${buildQuery({
          cursor: params.cursor,
          limit: params.limit,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          status: params.status,
        })}`,
      ),

    getRun: (id: string) => client.get<ReconciliationRunDto>(`${adminBase}/reconciliation/runs/${encodeURIComponent(id)}`),

    listRunRows: (id: string, params: ReconciliationRunRowsQuery = {}) =>
      client.get<ReconciliationCursorPage<ReconciliationSnapshotDto>>(
        `${adminBase}/reconciliation/runs/${encodeURIComponent(id)}/rows${buildQuery({
          cursor: params.cursor,
          limit: params.limit,
          scopeType: params.scopeType,
          assetCode: params.assetCode,
          networkCode: params.networkCode,
          merchantId: params.merchantId,
          type: params.type,
          status: params.status,
        })}`,
      ),

    exportRunCsv: (id: string, params: ReconciliationRunRowsQuery = {}) =>
      client.getBlob(
        `${adminBase}/reconciliation/runs/${encodeURIComponent(id)}/export${buildQuery({
          scopeType: params.scopeType,
          assetCode: params.assetCode,
          networkCode: params.networkCode,
          merchantId: params.merchantId,
          type: params.type,
          status: params.status,
        })}`,
      ),

    enqueueRun: (payload: EnqueueReconciliationRunPayload) =>
      client.post<ReconciliationRunDto>(`${adminBase}/reconciliation/runs`, payload),

    listDiscrepancies: (params: ReconciliationCasesQuery = {}) =>
      client.get<ReconciliationCursorPage<ReconciliationCaseDto>>(
        `${adminBase}/reconciliation/discrepancies${buildQuery({
          cursor: params.cursor,
          limit: params.limit,
          status: params.status,
          type: params.type,
          assetCode: params.assetCode,
          networkCode: params.networkCode,
          assignee: params.assignee,
          merchantId: params.merchantId,
        })}`,
      ),

    getDiscrepancy: (id: string) =>
      client.get<ReconciliationCaseDto>(`${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}`),

    assignDiscrepancy: (id: string, payload: AssignDiscrepancyPayload) =>
      client.post<ReconciliationCaseDto>(`${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}/assign`, payload),

    commentDiscrepancy: (id: string, payload: CommentDiscrepancyPayload) =>
      client.post<ReconciliationCaseDto>(`${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}/comments`, payload),

    resolveDiscrepancy: (id: string, payload: ResolveDiscrepancyPayload) =>
      client.post<ReconciliationCaseDto>(`${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}/resolve`, payload),

    previewAdjustment: (id: string, payload: ReconciliationAdjustmentPreviewPayload) =>
      client.post<ReconciliationDecisionDto>(
        `${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}/adjustments/preview`,
        payload,
      ),

    confirmAdjustment: (id: string, payload: ReconciliationAdjustmentConfirmPayload) =>
      client.post<ReconciliationAdjustmentConfirmResult>(
        `${adminBase}/reconciliation/discrepancies/${encodeURIComponent(id)}/adjustments`,
        payload,
      ),

    listSuspense: (params: SuspenseListQuery = {}) =>
      client.get<ReconciliationCursorPage<SuspenseItemDto>>(
        `${adminBase}/reconciliation/suspense${buildQuery({
          cursor: params.cursor,
          limit: params.limit,
          status: params.status,
          assetCode: params.assetCode,
          networkCode: params.networkCode,
        })}`,
      ),

    getSuspense: (id: string) =>
      client.get<SuspenseItemDto>(`${adminBase}/reconciliation/suspense/${encodeURIComponent(id)}`),

    assignSuspense: (id: string, payload: AssignSuspensePayload) =>
      client.post<SuspenseItemDto>(`${adminBase}/reconciliation/suspense/${encodeURIComponent(id)}/assign`, payload),

    listNetworkCosts: (params: NetworkCostsQuery = {}) =>
      client.get<ReconciliationCursorPage<NetworkCostGroupDto>>(
        `${adminBase}/network-costs${buildQuery({
          cursor: params.cursor,
          limit: params.limit,
          state: params.state,
          merchantId: params.merchantId,
          operationType: params.operationType,
          assetCode: params.assetCode,
        })}`,
      ),

    listPolicies: () => client.get<{ data: NetworkCostPolicyDto[] }>(`${adminBase}/network-cost-policies`),

    updatePolicy: (scopeKey: string, payload: UpdateNetworkCostPolicyPayload) =>
      client.put<NetworkCostPolicyDto>(
        `${adminBase}/network-cost-policies/${encodeURIComponent(scopeKey)}`,
        payload,
      ),

    createChallenge: (payload: ReconciliationChallengeRequest) =>
      client.post<ReconciliationChallengeDto>(`${adminBase}/reconciliation/challenges`, payload),
  };
}

export type ReconciliationService = ReturnType<typeof createReconciliationService>;
